import ObjectScreen from '@components/v2/layout/ObjectScreen/ObjectScreen';
import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import { Stack } from '@mui/material';
import { Plus, RefreshCw } from 'lucide-react';
import LangEnum from '../../../../../../../../lang/LangEnum';
import { CatalogProductsTestIds } from '../../../../../../../../utils/testIds/CatalogProducts.testIds';
import { useTranslation } from '../../../../../../../translation/TranslationContext';
import { Catalog } from '../../../../../../catalog.types';
import CatalogCreationCriteriaChip from '../../../../../../creation/components/CatalogCreationCriteriaChip';
import CatalogCreationCriteriaPopover from '../../../../../../creation/components/CatalogCreationCriteriaPopover';
import useCatalogAutoInclusionSection from './hooks/useCatalogAutoInclusionSection';

export type CatalogAutoInclusionSectionProps = Pick<
  Catalog,
  | 'id'
  | 'availability'
  | 'collections'
  | 'classifications'
  | 'availabilityCriteriaOperator'
  | 'classificationCriteriaOperator'
> & {
  isReadOnly: boolean;
};

const CatalogAutoInclusionSection = (props: CatalogAutoInclusionSectionProps) => {
  const { translate } = useTranslation();
  const {
    chips,
    anchorEl,
    activeCriterion,
    draftValues,
    draftOperator,
    isOpen,
    isDisabled,
    canAddCriterion,
    canSyncCatalog,
    isSyncing,
    optionLabelsById,
    openCriteriaTypes,
    openCriterion,
    closeCriteria,
    backToCriteriaTypes,
    selectCriterion,
    changeDraftOperator,
    toggleDraftOption,
    applyDraft,
    removeCriterion,
    syncCatalog,
  } = useCatalogAutoInclusionSection(props);

  const hasChips = chips.length > 0;

  return (
    <ObjectScreen.Section
      title={LangEnum.GLOBAL_PRODUCTS}
      isDefaultOpen
      persistKey="catalog-details:auto-inclusion"
      action={
        canSyncCatalog && (
          <IconButton
            icon={RefreshCw}
            size="medium"
            disabled={isSyncing}
            testId={CatalogProductsTestIds.DETAILS_SYNC}
            ariaLabel={translate(LangEnum.GLOBAL_FORCE_SYNC)}
            onClick={() => syncCatalog()}
          />
        )
      }
    >
      <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        {chips.map((chip) => (
          <CatalogCreationCriteriaChip
            key={chip.criterion}
            chip={chip}
            optionLabelsById={optionLabelsById}
            onEdit={isDisabled ? undefined : openCriterion}
            onRemove={isDisabled ? undefined : removeCriterion}
          />
        ))}

        {canAddCriterion && !isDisabled && hasChips && (
          <IconButton
            icon={Plus}
            size="small"
            color="secondary"
            testId={CatalogProductsTestIds.DETAILS_CRITERIA_ADD}
            ariaLabel={translate(LangEnum.CATALOGS_CREATION_MODAL_ADD_CRITERIA)}
            onClick={(event) => openCriteriaTypes(event.currentTarget)}
          />
        )}

        {canAddCriterion && !isDisabled && !hasChips && (
          <Button
            color="secondary"
            size="small"
            startIcon={<Icon icon={Plus} size="sm" />}
            testId={CatalogProductsTestIds.DETAILS_CRITERIA_ADD}
            onClick={(event) => openCriteriaTypes(event.currentTarget)}
          >
            {translate(LangEnum.CATALOGS_CREATION_MODAL_ADD_CRITERIA)}
          </Button>
        )}
      </Stack>

      <CatalogCreationCriteriaPopover
        isOpen={isOpen}
        anchorEl={anchorEl}
        activeCriterion={activeCriterion}
        selectedIds={draftValues}
        optionLabelsById={optionLabelsById}
        operator={draftOperator}
        onClose={closeCriteria}
        onSelectCriterion={selectCriterion}
        onBack={backToCriteriaTypes}
        onApply={applyDraft}
        onOperatorChange={changeDraftOperator}
        onToggleOption={toggleDraftOption}
      />
    </ObjectScreen.Section>
  );
};

export default CatalogAutoInclusionSection;
